import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import monthlyCloseoutService from 'src/services/apiServices/monthlyCloseOut/monthlyCloseoutService';

const CLOSED_STATUS = 'CLOSED_AND_SIGNED';

/**
 * Which closeout divisions can lock a note.
 *
 * MONTHLY alone was wrong. A closeout period is an arbitrary date range, and the Custom range
 * feature (BOPS-115) creates them with any start/end inside a month that is otherwise still
 * open — so a note dated 08-19 could be locked while 08-18 beside it was not, and asking only
 * for MONTHLY could not see the difference. Confirmed with BE, 21 Aug.
 */
const DIVISIONS = ['MONTHLY', 'CUSTOM'];

/**
 * 'YYYY-MM' for a document's date of service.
 *
 * The API sends the date as a UTC instant but the lists render it as a plain calendar date
 * (see completed-document-list), so the month has to be read the same way the user sees it —
 * otherwise a note on the 1st or the last day of a month lands in the neighbouring one.
 */
export const dayKeyOfDateOfService = (dateOfService: any): string => {
  if (!dateOfService) return '';
  if (typeof dateOfService === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateOfService)) {
    return dateOfService.substring(0, 10);
  }
  const parsed = new Date(dateOfService);
  if (Number.isNaN(parsed.valueOf())) return '';
  return moment(parsed.valueOf() + parsed.getTimezoneOffset() * 60 * 1000).format('YYYY-MM-DD');
};

export const monthKeyOfDateOfService = (dateOfService: any): string =>
  dayKeyOfDateOfService(dateOfService).substring(0, 7);

// Two document lists mount together on the documentation tab; share the request they both
// make instead of asking for the same closeout list twice. Only in-flight requests are
// shared — a resolved one is dropped so re-opening a month is picked up on the next mount.
type ClosedPeriods = {
  /** 'YYYY-MM' for whole months closed on the Monthly tab. */
  months: Set<string>;
  /** Inclusive 'YYYY-MM-DD' bounds for custom periods, which need not align to a month. */
  ranges: Array<{ start: string; end: string }>;
};

const inFlightRequests = new Map<string, Promise<ClosedPeriods>>();

const fetchClosedPeriods = (cacheKey: string, currentDate: string): Promise<ClosedPeriods> => {
  const pending = inFlightRequests.get(cacheKey);
  if (pending) return pending;

  //  One request per division, each failing on its own. A rejection must not take the other
  //  division down with it: Custom is the newer of the two and not every enterprise has it, so
  //  letting a Custom error reject the pair would drop the Monthly result as well and silently
  //  switch off the closed-month protection that has been in place since BOPS-108.
  const request = Promise.all(
    DIVISIONS.map((division) =>
      monthlyCloseoutService
        .getMonthlyCloseOut({ param: { currentDate, division } })
        .then((response: any) => ({ division, periods: response?.data?.data }))
        .catch(() => ({ division, periods: [] as any[] }))
    )
  )
    .then((results) => {
      const closed: ClosedPeriods = { months: new Set<string>(), ranges: [] };
      results.forEach(({ division, periods }) => {
        if (!Array.isArray(periods)) return;
        periods.forEach((period: any) => {
          if (period?.status !== CLOSED_STATUS) return;
          const start = period?.range?.startDate?.substring(0, 10);
          const end = period?.range?.endDate?.substring(0, 10);
          if (division === 'MONTHLY') {
            //  A monthly period is the whole month whatever its range says, so it is kept as a
            //  month key — cheaper to match and unaffected by how the range is expressed.
            if (start) closed.months.add(start.substring(0, 7));
            return;
          }
          if (start && end) closed.ranges.push({ start, end });
        });
      });
      return closed;
    })
    .finally(() => {
      inFlightRequests.delete(cacheKey);
    });

  inFlightRequests.set(cacheKey, request);
  return request;
};

/**
 * Which dates are inside a closed-and-signed period for the current facility (BOPS-108).
 *
 * Same source the platform already uses to grey out Start Note and drop the edit action on a
 * closed month: GET /provider/month-end-alert/closeout, MONTHLY, anchored on today — which
 * covers the current month plus the twelve before it.
 *
 * @param enabled skip the request entirely when the caller has nothing to gate on it.
 */
const useClosedMonths = (enabled: boolean) => {
  const [closedPeriods, setClosedPeriods] = useState<ClosedPeriods | null>(null);
  const facilityId = localStorage.getItem('facilityId') || '';

  useEffect(() => {
    if (!enabled) {
      setClosedPeriods(null);
      return;
    }

    let cancelled = false;
    const currentDate = moment().format('YYYY-MM-DD');

    fetchClosedPeriods(`${facilityId}|${currentDate}`, currentDate)
      .then((periods) => {
        if (!cancelled) setClosedPeriods(periods);
      })
      .catch(() => {
        // Endpoint unreachable: fall back to leaving actions available, the way they were
        // before this gate existed. The backend still rejects a closed-period change.
        if (!cancelled) setClosedPeriods({ months: new Set(), ranges: [] });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, facilityId]);

  const isDateInClosedPeriod = useCallback(
    (dateOfService: any) => {
      if (!enabled) return false;
      const dayKey = dayKeyOfDateOfService(dateOfService);
      if (!dayKey) return false;
      // Still unknown while the lists are in flight: withhold the action rather than offer one
      // the backend would reject.
      if (closedPeriods === null) return true;
      if (closedPeriods.months.has(dayKey.substring(0, 7))) return true;
      //  Bounds are inclusive both ends, and 'YYYY-MM-DD' compares correctly as a string.
      return closedPeriods.ranges.some(({ start, end }) => dayKey >= start && dayKey <= end);
    },
    [enabled, closedPeriods]
  );

  return { isDateInClosedPeriod };
};

export default useClosedMonths;
